// DocSharkResults.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './DocSharkResults.css';
import { getHighlights, getImageHighlightsFromAPI, getImagesBatchFromAPI, getFullImageFromAPI } from './DocSharkAPI';
import { useStructureMapping } from '../../shared/contexts/StructureContext';
import { BarChart, Bar, LabelList, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { exportToCSVUtility, exportToExcelUtility, makeEMRDselectionUtility } from './exportUtils';

// To manipulate selected medicines
import { useCheckedState } from '../../shared/contexts/CheckedContext';

// Import new components
import ResultsHeader from './components/results/ResultsHeader';
import ExportControls from './components/results/ExportControls';
import ResultsFilterControls from './components/results/ResultsFilterControls';
import Pagination from './components/common/Pagination';
import EuNumberGroup from './components/results/EuNumberGroup';
import ImageModal from './components/modals/ImageModal';
import { usePaginatedFilteredSortedData } from './hooks/usePaginatedFilteredSortedData';

const DocSharkResults = ({ searchResults, searchQuery, displayedSearchQuery, allData, selectedData = [], includeImages: propIncludeImages, includeTermFrequencies: propIncludeTermFrequencies, selectedScope = 'all', selectedScopeCount = 0 }) => {
  const { search_time, results, includeImages = propIncludeImages, includeTermFrequencies = propIncludeTermFrequencies } = searchResults;
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [highlightsMap, setHighlightsMap] = useState({});
  const [loadingHighlights, setLoadingHighlights] = useState({});
  const [highlightErrors, setHighlightErrors] = useState({});

  const [imageDataUriMap, setImageDataUriMap] = useState({});
  const [loadingGroupImages, setLoadingGroupImages] = useState(false);
  const [loadingModalImage, setLoadingModalImage] = useState(false); // New state for modal image loading

  const [selectedMedicines, setSelectedMedicines] = useState({});
  const [selectAllInternal, setSelectAllInternal] = useState(false);
  const [includeProductData, setIncludeProductData] = useState(false);

  // Modal State
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentModalImage, setCurrentModalImage] = useState('');
  const [currentModalImageAlt, setCurrentModalImageAlt] = useState('');
  const [currentModalCaption, setCurrentModalCaption] = useState('');
  const [currentModalPageNumber, setCurrentModalPageNumber] = useState(null);
  const [currentModalDocFilename, setCurrentModalDocFilename] = useState(null);
  const [currentModalImagePath, setCurrentModalImagePath] = useState(null);
  const [currentModalDocLink, setCurrentModalDocLink] = useState(null);
  const [currentModalDocName, setCurrentModalDocName] = useState(null);
  const [modalImageDynamicHighlights, setModalImageDynamicHighlights] = useState([]);
  const [isModalImageDynamicHighlightsLoading, setIsModalImageDynamicHighlightsLoading] = useState(false);
  const [modalImageDynamicHighlightsError, setModalImageDynamicHighlightsError] = useState(null);

  const [showSearchOverview, setShowSearchOverview] = useState(false);

  // New navigation state
  const [currentModalImageIndex, setCurrentModalImageIndex] = useState(0);
  const [currentModalImagesArray, setCurrentModalImagesArray] = useState([]);

  const { checkedState, setCheckedState } = useCheckedState()

  const groupedResults = useMemo(() => {
    const groups = {};
    results.forEach(result => {
      const euNumber = result.eu_pnumber;
      const docFilenameKey = result.original_pdf_filename || result.filename;
      if (!euNumber || !docFilenameKey) return;
      if (!groups[euNumber]) groups[euNumber] = {};
      if (!groups[euNumber][docFilenameKey]) {
        groups[euNumber][docFilenameKey] = { details: {}, images: [] };
      }
      if (result._isImageHit && result.image_path) {
        if (!groups[euNumber][docFilenameKey].images.some(img => img.image_path === result.image_path)) {
          groups[euNumber][docFilenameKey].images.push({
            image_path: result.image_path,
            page: result.page_number || null,
            caption: result.image_caption || '',
            matched_terms: result.matched_terms || [],
          });
        }
        if (Object.keys(groups[euNumber][docFilenameKey].details).length === 0) {
          groups[euNumber][docFilenameKey].details = {
            eu_pnumber: result.eu_pnumber, original_pdf_filename: result.original_pdf_filename,
            filename: result.filename, file_type: result.file_type, real_name: result.real_name, file_link: result.file_link,
          };
        }
      } else if (!result._isImageHit) {
        groups[euNumber][docFilenameKey].details = {
          ...groups[euNumber][docFilenameKey].details, ...result,
          original_pdf_filename: result.original_pdf_filename || result.filename, filename: result.filename
        };
      }
    });
    for (const euKey in groups) {
      for (const docKey in groups[euKey]) {
        if (groups[euKey][docKey].images) {
          groups[euKey][docKey].images.sort((a, b) => {
            if (a.page === null && b.page === null) return 0; if (a.page === null) return 1; if (b.page === null) return -1;
            return a.page - b.page;
          });
        }
      }
    }
    return groups;
  }, [results]);

  // Use the custom hook
  const {
    filterText,
    setFilterText,
    debouncedFilterText,
    sortOption,
    sortDirection,
    handleSortChange,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    paginatedEuGroups,
    totalPages,
    filteredAndSortedEuGroups,
  } = usePaginatedFilteredSortedData(groupedResults, allData, 10);

  const totalMedicines = useMemo(() => Object.keys(groupedResults).length, [groupedResults]);
  const totalDocuments = useMemo(() => {
    let count = 0;
    Object.values(groupedResults).forEach(euGroup => count += Object.keys(euGroup).length);
    return count;
  }, [groupedResults]);

  const totalImages = useMemo(() => {
    let count = 0;
    Object.values(groupedResults).forEach(euGroup =>
      Object.values(euGroup).forEach(docGroup => count += docGroup.images.length)
    );
    return count;
  }, [groupedResults]);

  // Collect all matched_terms from documents
  const matchedTerms = useMemo(() => {
    const terms = [];
    Object.values(groupedResults).forEach(euGroup => {
      Object.values(euGroup).forEach(docGroup => {
        if (docGroup.details?.matched_terms) {
          terms.push(docGroup.details.matched_terms);
        }
        // Also collect matched_terms from images (if present)
        if (Array.isArray(docGroup.images)) {
          docGroup.images.forEach(img => {
            if (img?.matched_terms) terms.push(img.matched_terms);
          });
        }
      });
    });
    return terms;
  }, [groupedResults]);

  const fileTypeCounts = useMemo(() => {
    const counts = {};
    Object.values(groupedResults).forEach(euGroup =>
      Object.values(euGroup).forEach(docGroup => {
        if (docGroup.details && docGroup.details.file_type) {
          const fileType = docGroup.details.file_type === 'Nan' ? 'other' : docGroup.details.file_type;
          counts[fileType] = (counts[fileType] || 0) + 1;
        } else if (docGroup.details) {
          counts['other'] = (counts['other'] || 0) + 1;
        }
      })
    );
    return counts;
  }, [groupedResults]);

  const sourceData = selectedScope === 'current' ? selectedData : allData;

  const sourceDataYearCounts = useMemo(() => {
    const counts = {};
    if (!Array.isArray(sourceData)) return counts;
    sourceData.forEach(item => {
      const date = item.eu_aut_date;
      if (!date || typeof date !== 'string') return;
      const year = date.split('-')[0];
      if (!year || Number.isNaN(Number(year))) return;
      counts[year] = (counts[year] || 0) + 1;
    });
    return counts;
  }, [sourceData]);

  const searchYearCounts = useMemo(() => {
    const counts = {};
    if (!Array.isArray(results) || !Array.isArray(allData)) return counts;

    // Helper to make "EU-1-23" and "EU/1/23" both "EU123"
    const normalize = (id) => (id ? String(id).replace(/[^a-zA-Z0-9]/g, '').toUpperCase() : '');

    // 1. Create a lookup map using normalized keys from allData
    const medicineDateMap = {};
    allData.forEach(item => {
      const rawId = item.eu_pnumber || item.eu_number || item.euNumber;
      const normalizedId = normalize(rawId);
      if (normalizedId) {
        medicineDateMap[normalizedId] = item.eu_aut_date || item.euDate || item.authorisation_date;
      }
    });

    const seenMedicineYear = new Set();

    results.forEach(item => {
      const rawId = item.eu_pnumber || item.eu_number || item.euNumber;
      const normalizedId = normalize(rawId);
      if (!normalizedId) return;

      // 2. Look up the date using the normalized ID
      const date = medicineDateMap[normalizedId];

      if (!date || typeof date !== 'string') return;
      const year = date.split('-')[0];
      if (!year || Number.isNaN(Number(year))) return;

      // Ensure we only count each unique medicine once for the search results bar
      const setKey = `${normalizedId}|${year}`;
      if (seenMedicineYear.has(setKey)) return;

      seenMedicineYear.add(setKey);
      counts[year] = (counts[year] || 0) + 1;
    });

    return counts;
  }, [results, allData]);

  const searchYearsOverview = useMemo(() => {
    const uniqueYears = new Set([...Object.keys(sourceDataYearCounts), ...Object.keys(searchYearCounts)]);
    const sortedYears = Array.from(uniqueYears).sort((a, b) => Number(a) - Number(b));
    return sortedYears.map(year => ({
      year,
      selection: sourceDataYearCounts[year] || 0,
      search: searchYearCounts[year] || 0,
    }));
  }, [sourceDataYearCounts, searchYearCounts]);

  const totalAllMedicines = Array.isArray(allData) ? allData.length : 0;
  const totalScopeMedicines = selectedScope === 'current' ? selectedScopeCount : totalAllMedicines;
  const totalSearchMedicines = totalMedicines;
  const searchCoverage = totalScopeMedicines > 0 ? ((totalSearchMedicines / totalScopeMedicines) * 100).toFixed(1) : '0.0';

  const dataKeyToValueMap = useStructureMapping();
  const selectedCount = useMemo(() => Object.values(selectedMedicines).filter(Boolean).length, [selectedMedicines]);

  useEffect(() => {
    setSelectAllInternal(filteredAndSortedEuGroups.length > 0 && filteredAndSortedEuGroups.every(g => selectedMedicines[g.euNumber]));
  }, [selectedMedicines, filteredAndSortedEuGroups]);

  const handleSelectAllChange = useCallback(() => {
    const newAll = !selectAllInternal;
    setSelectAllInternal(newAll);
    const newSelected = {};
    if (newAll) {
      filteredAndSortedEuGroups.forEach(g => { newSelected[g.euNumber] = true; });
    }
    setSelectedMedicines(newSelected);
  }, [selectAllInternal, filteredAndSortedEuGroups]);

  const handleMedicineSelectChange = useCallback((euNumber, isSelected) => {
    setSelectedMedicines(prev => ({ ...prev, [euNumber]: isSelected }));
  }, []);

  const loadGroupHighlights = useCallback(async (euNum) => {
    const documentsMap = groupedResults[euNum];
    if (!documentsMap) return;
    const docFilenamesToFetch = Object.values(documentsMap)
      .map(docGroup => docGroup.details?.original_pdf_filename || docGroup.details?.filename)
      .filter(Boolean);

    if (docFilenamesToFetch.length === 0) return;

    setLoadingHighlights(prev => ({ ...prev, ...docFilenamesToFetch.reduce((acc, fname) => ({ ...acc, [fname]: true }), {}) }));
    setHighlightErrors(prev => {
      const newState = { ...prev };
      docFilenamesToFetch.forEach(fname => delete newState[fname]);
      return newState;
    });

    const promises = docFilenamesToFetch.map(async (fname) => {
      try {
        const data = await getHighlights(fname, searchQuery, 5);
        return { fileName: fname, highlights: data.highlights || [] };
      } catch (error) {
        console.error(`Failed to fetch text highlights for ${fname}:`, error);
        setHighlightErrors(prevErr => ({ ...prevErr, [fname]: "Could not load text highlights." }));
        return { fileName: fname, highlights: [] };
      }
    });
    try {
      const h_results = await Promise.all(promises);
      setHighlightsMap(prevMap => {
        const newMap = { ...prevMap };
        h_results.forEach(r => { newMap[r.fileName] = r.highlights; });
        return newMap;
      });
    } catch (e) { console.error("Error loading text highlights batch:", e); }
    setLoadingHighlights(prev => ({ ...prev, ...docFilenamesToFetch.reduce((acc, fname) => ({ ...acc, [fname]: false }), {}) }));
  }, [groupedResults, searchQuery]);

  const loadGroupImages = useCallback(async (euNum) => {
    const documentsMap = groupedResults[euNum];
    if (!documentsMap) return;

    const imagePathsToFetch = [];
    Object.values(documentsMap).forEach(docGroup => {
      docGroup.images.forEach(image => {
        if (!imageDataUriMap[image.image_path]) {
          imagePathsToFetch.push(image.image_path);
        }
      });
    });

    if (imagePathsToFetch.length === 0) return;
    setLoadingGroupImages(true);
    // Request thumbnails for initial display
    const THUMBNAIL_SIZE = 200; // Define your desired thumbnail size
    try {
      const batchResponse = await getImagesBatchFromAPI(imagePathsToFetch, THUMBNAIL_SIZE);
      setImageDataUriMap(prevMap => {
        const newMap = { ...prevMap };
        batchResponse.images.forEach(imgData => {
          newMap[imgData.image_path] = { uri: imgData.image_data_uri, error: imgData.error };
        });
        return newMap;
      });
    } catch (error) {
      console.error("Error loading batch images for group:", error);
      setImageDataUriMap(prevMap => {
        const newMap = { ...prevMap };
        imagePathsToFetch.forEach(path => {
          if (!newMap[path]) { newMap[path] = { uri: null, error: "Batch fetch failed" }; }
        });
        return newMap;
      });
    } finally {
      setLoadingGroupImages(false);
    }
  }, [groupedResults, imageDataUriMap]);

  const toggleGroup = useCallback(async (euNumber) => {
    const newExpanded = expandedGroup === euNumber ? null : euNumber;
    setExpandedGroup(newExpanded);
    if (newExpanded && groupedResults[newExpanded]) {
      const docsInGroup = groupedResults[newExpanded];
      let highlightsNeeded = false;
      for (const docKey in docsInGroup) {
        const docDetails = docsInGroup[docKey].details;
        const filenameKey = docDetails.original_pdf_filename || docDetails.filename;
        if (filenameKey && !highlightsMap[filenameKey] && !loadingHighlights[filenameKey]) {
          highlightsNeeded = true;
          break;
        }
      }
      if (highlightsNeeded) {
        await loadGroupHighlights(newExpanded);
      }
      // Load thumbnails when the group is expanded
      await loadGroupImages(newExpanded);
    }
  }, [expandedGroup, groupedResults, highlightsMap, loadingHighlights, loadGroupHighlights, loadGroupImages]);

  const fetchImageHighlightsForModal = useCallback(async (imagePath) => {
    if (!imagePath || !searchQuery) return;
    setIsModalImageDynamicHighlightsLoading(true);
    setModalImageDynamicHighlightsError(null);
    setModalImageDynamicHighlights([]);
    try {
      const data = await getImageHighlightsFromAPI(imagePath, searchQuery);
      setModalImageDynamicHighlights(data.highlights || []);
    } catch (error) {
      console.error("Failed to fetch image highlights for modal:", error);
      setModalImageDynamicHighlightsError("Could not load specific image highlights.");
    } finally {
      setIsModalImageDynamicHighlightsLoading(false);
    }
  }, [searchQuery]);

  // Helper function to build the full images array for a document
  const buildImagesArrayForDocument = useCallback((docGroup, docFilename, docDetails) => {
    return docGroup.images.map((image, index) => ({
      ...image,
      altText: `Thumbnail ${index + 1} from ${docDetails?.real_name || docFilename}${image.page ? ` (Page ${image.page})` : ''}`,
      originalDocFilename: docDetails?.original_pdf_filename || docDetails?.filename || docFilename,
      docLink: typeof docDetails?.file_link === 'string' && docDetails.file_link !== 'Nan' ? docDetails.file_link : null,
      docName: docDetails?.real_name || docFilename,
      matched_terms: image.matched_terms || [],
    }));
  }, []);

  const openImageModal = useCallback(async (imagePath, altText, caption, pageNumber, docFilename, docLink, docName) => {
    // Find the document group that contains this image
    let targetDocGroup = null;
    let targetDocDetails = null;
    let targetDocFilename = null;

    for (const euNumber in groupedResults) {
      for (const docKey in groupedResults[euNumber]) {
        const docGroup = groupedResults[euNumber][docKey];
        if (docGroup.images.some(img => img.image_path === imagePath)) {
          targetDocGroup = docGroup;
          targetDocDetails = docGroup.details;
          targetDocFilename = docKey;
          break;
        }
      }
      if (targetDocGroup) break;
    }

    if (targetDocGroup && targetDocDetails) {
      // Build the full images array for this document
      const imagesArray = buildImagesArrayForDocument(targetDocGroup, targetDocFilename, targetDocDetails);

      // Find the index of the current image
      const currentIndex = imagesArray.findIndex(img => img.image_path === imagePath);

      setCurrentModalImagesArray(imagesArray);
      setCurrentModalImageIndex(currentIndex >= 0 ? currentIndex : 0);

      // Request full resolution image for the modal
      setLoadingModalImage(true);
      try {
        const fullImageResponse = await getFullImageFromAPI(imagePath);
        if (fullImageResponse.image && fullImageResponse.image.image_data_uri) {
          setCurrentModalImage(fullImageResponse.image.image_data_uri);
        } else {
          setCurrentModalImage(''); // Fallback if full image fails
          console.error("Failed to load full resolution image for modal:", fullImageResponse.image?.error || "Unknown error");
        }
      } catch (error) {
        setCurrentModalImage('');
        console.error("Error fetching full image for modal:", error);
      } finally {
        setLoadingModalImage(false);
      }

      setCurrentModalImageAlt(altText);
      setCurrentModalCaption(caption);
      setCurrentModalPageNumber(pageNumber);
      setCurrentModalDocFilename(docFilename);
      setCurrentModalImagePath(imagePath);
      setCurrentModalDocLink(docLink);
      setCurrentModalDocName(docName);
      setIsImageModalOpen(true);

      fetchImageHighlightsForModal(imagePath);
    }
  }, [groupedResults, buildImagesArrayForDocument, fetchImageHighlightsForModal]);

  // Navigation functions
  const handlePreviousImage = useCallback(async () => {
    if (currentModalImageIndex > 0) {
      const newIndex = currentModalImageIndex - 1;
      const newImage = currentModalImagesArray[newIndex];

      setCurrentModalImageIndex(newIndex);
      setCurrentModalImageAlt(newImage.altText);
      setCurrentModalCaption(newImage.caption);
      setCurrentModalPageNumber(newImage.page);
      setCurrentModalImagePath(newImage.image_path);

      // Request full resolution image for the new image in modal
      setLoadingModalImage(true);
      try {
        const fullImageResponse = await getFullImageFromAPI(newImage.image_path);
        if (fullImageResponse.image && fullImageResponse.image.image_data_uri) {
          setCurrentModalImage(fullImageResponse.image.image_data_uri);
        } else {
          setCurrentModalImage('');
          console.error("Failed to load full resolution image for previous modal image:", fullImageResponse.image?.error || "Unknown error");
        }
      } catch (error) {
        setCurrentModalImage('');
        console.error("Error fetching full image for previous modal image:", error);
      } finally {
        setLoadingModalImage(false);
      }

      fetchImageHighlightsForModal(newImage.image_path);
    }
  }, [currentModalImageIndex, currentModalImagesArray, fetchImageHighlightsForModal]);

  const handleNextImage = useCallback(async () => {
    if (currentModalImageIndex < currentModalImagesArray.length - 1) {
      const newIndex = currentModalImageIndex + 1;
      const newImage = currentModalImagesArray[newIndex];

      setCurrentModalImageIndex(newIndex);
      setCurrentModalImageAlt(newImage.altText);
      setCurrentModalCaption(newImage.caption);
      setCurrentModalPageNumber(newImage.page);
      setCurrentModalImagePath(newImage.image_path);

      // Request full resolution image for the new image in modal
      setLoadingModalImage(true);
      try {
        const fullImageResponse = await getFullImageFromAPI(newImage.image_path);
        if (fullImageResponse.image && fullImageResponse.image.image_data_uri) {
          setCurrentModalImage(fullImageResponse.image.image_data_uri);
        } else {
          setCurrentModalImage('');
          console.error("Failed to load full resolution image for next modal image:", fullImageResponse.image?.error || "Unknown error");
        }
      } catch (error) {
        setCurrentModalImage('');
        console.error("Error fetching full image for next modal image:", error);
      } finally {
        setLoadingModalImage(false);
      }

      fetchImageHighlightsForModal(newImage.image_path);
    }
  }, [currentModalImageIndex, currentModalImagesArray, fetchImageHighlightsForModal]);

  const closeImageModal = useCallback(() => {
    setIsImageModalOpen(false);
    setCurrentModalImage('');
    setCurrentModalImageAlt('');
    setCurrentModalCaption('');
    setCurrentModalPageNumber(null);
    setCurrentModalDocFilename(null);
    setCurrentModalImagePath(null);
    setCurrentModalDocLink(null);
    setCurrentModalDocName(null);
    setModalImageDynamicHighlights([]);
    setIsModalImageDynamicHighlightsLoading(false);
    setModalImageDynamicHighlightsError(null);
    setCurrentModalImagesArray([]);
    setCurrentModalImageIndex(0);
    setLoadingModalImage(false); // Reset modal image loading state
  }, []);

  const exportToCSV = useCallback(() => exportToCSVUtility(filteredAndSortedEuGroups, selectedMedicines, dataKeyToValueMap, allData, includeProductData, includeTermFrequencies, displayedSearchQuery), [filteredAndSortedEuGroups, selectedMedicines, dataKeyToValueMap, allData, includeProductData, includeTermFrequencies, displayedSearchQuery]);
  const exportToExcel = useCallback(() => exportToExcelUtility(filteredAndSortedEuGroups, selectedMedicines, dataKeyToValueMap, allData, includeProductData, includeTermFrequencies, groupedResults, displayedSearchQuery), [filteredAndSortedEuGroups, selectedMedicines, dataKeyToValueMap, allData, includeProductData, includeTermFrequencies, groupedResults, displayedSearchQuery]);
  const makeEMRDselection = useCallback(() => makeEMRDselectionUtility(selectedMedicines, checkedState, setCheckedState));

  return (
    <div className="research-results-box search-results-container">
      <ResultsHeader
        displayedSearchQuery={displayedSearchQuery}
        searchTime={search_time}
        totalMedicines={totalMedicines}
        totalDocuments={totalDocuments}
        totalImages={totalImages}
        fileTypeCounts={fileTypeCounts}
        matchedTerms={matchedTerms}
        includeTermFrequencies={includeTermFrequencies}
        showSearchOverview={showSearchOverview}
        onToggleSearchOverview={() => setShowSearchOverview(p => !p)}
      />

      {showSearchOverview && (
        <div className="docshark-search-overview" style={{ marginBottom: '16px', padding: '16px', backgroundColor: 'var(--secondary-light)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <div>
              <h3 style={{ margin: '0 0 4px 0' }}>Search population coverage</h3>
              <p style={{ margin: 0 }}>
                <strong>{totalSearchMedicines}</strong> of {selectedScope === 'current' ? '' : 'all'} <strong>{totalScopeMedicines}</strong> {selectedScope === 'current' ? 'EMRD-selected medicines' : 'authorised medicines'}
                {' '}({searchCoverage}%) are returned by this search.
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.9rem'}}><strong>Authorisation year breakdown</strong></p>
            </div>
          </div>

          <div style={{ width: '100%', height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={searchYearsOverview} margin={{ top: 10, right: 30, left: 0, bottom: 15 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="year"
                  label={{ value: 'Authorisation year', position: 'insideBottom', offset: -10, fill: 'var(--text-primary)', style: { fontWeight: 600 } }}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  tick={{ fill: 'var(--text-primary)' }}
                />
                <YAxis
                  tick={{ fill: 'var(--text-primary)' }}
                  label={{
                    value: 'Medicine Count',
                    angle: -90,
                    position: 'insideLeft',
                    fill: 'var(--text-primary)',
                    dy: 0,
                    dx: 14,
                    offset: 0,
                    style: { fontWeight: 600, textAnchor: 'middle' }
                  }}
                />
                <Tooltip
                  formatter={(value) => Number(value).toLocaleString()}
                  contentStyle={{ color: 'var(--text-primary)', backgroundColor:'var(--secondary-light)', borderColor:'var(--border-color)' }}
                />
                <Legend verticalAlign="top" wrapperStyle={{ color: 'var(--text-primary)' }} />
                <Bar dataKey="selection" name={selectedScope === 'current' ? 'Selected medicines' : 'All authorised medicines'} fill="#88b4d8" />
                <Bar dataKey="search" name="Medicines in search results" fill="#82ca9d">
                  <LabelList
                    dataKey="search"
                    position="top"
                    fill="var(--text-primary)"
                    style={{ fontSize: '0.8rem', fontWeight: 600 }}
                    formatter={(value) => (value > 0 ? value : '')}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <ExportControls
        selectAll={selectAllInternal}
        onSelectAllChange={handleSelectAllChange}
        selectedCount={selectedCount}
        makeSelection={makeEMRDselection}
        onExportCSV={exportToCSV}
        onExportExcel={exportToExcel}
        includeProductData={includeProductData}
        onIncludeProductDataChange={() => setIncludeProductData(p => !p)}
        totalFilteredCount={filteredAndSortedEuGroups.length}
      />

      <ResultsFilterControls
        filterText={filterText}
        onFilterTextChange={setFilterText}
        sortOption={sortOption}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        totalFilteredResults={filteredAndSortedEuGroups.length}
        paginatedResultsCount={paginatedEuGroups.length}
        displayedFilterText={debouncedFilterText}
        includeImages={includeImages}
      />

      {totalPages > 1 && (
        <div className="pagination-top">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
          />
        </div>
      )}

      <div className="results-by-eu-number">
        {paginatedEuGroups.length > 0 ? (
          paginatedEuGroups.map(({ euNumber, documentsMap }) => (
            <EuNumberGroup
              key={euNumber}
              euNumber={euNumber}
              documentsMap={documentsMap}
              allData={allData}
              isExpanded={expandedGroup === euNumber}
              onToggleGroup={toggleGroup}
              isSelected={!!selectedMedicines[euNumber]}
              onSelectChange={handleMedicineSelectChange}
              highlightsMap={highlightsMap}
              loadingHighlights={loadingHighlights}
              highlightErrors={highlightErrors}
              imageDataUriMap={imageDataUriMap}
              onOpenImageModal={openImageModal}
              loadingGroupImages={expandedGroup === euNumber && loadingGroupImages}
            />
          ))
        ) : (
          <div className="no-results-message">
            <i className='bx bx-search-alt'></i><p>No medicines match your criteria.</p>
            {filterText && (<button className="clear-filter-button" onClick={() => setFilterText('')}>Clear Filter</button>)}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination-bottom">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }}
          />
        </div>
      )}

      <ImageModal
        isOpen={isImageModalOpen}
        onClose={closeImageModal}
        imageSrc={currentModalImage}
        altText={currentModalImageAlt}
        caption={currentModalCaption}
        pageNumber={currentModalPageNumber}
        docName={currentModalDocName}
        docLink={currentModalDocLink}
        imageMatchedTerms={currentModalImagesArray[currentModalImageIndex]?.matched_terms || []}
        dynamicHighlights={modalImageDynamicHighlights}
        isLoadingDynamicHighlights={isModalImageDynamicHighlightsLoading}
        dynamicHighlightsError={modalImageDynamicHighlightsError}
        // Navigation props
        currentImageIndex={currentModalImageIndex}
        totalImages={currentModalImagesArray.length}
        onPreviousImage={handlePreviousImage}
        onNextImage={handleNextImage}
        hasMultipleImages={currentModalImagesArray.length > 1}
        isLoadingModalImage={loadingModalImage}
      />
    </div>
  );
};

export default DocSharkResults;